import React from "react";
import { ValueType, components } from "react-select";
import { MultiValueProps } from "react-select/src/components/MultiValue";
import { OptionProps } from "react-select/src/components/Option";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FieldInputProps } from "react-final-form";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { Button, Typography } from "@material-ui/core";

import FormControlError from "components/FormControl/FormControlError";
import TrimmedText from "components/TrimmedText";
import ConfirmationDialog from "components/ConfirmationDialog";
import Popup from "components/Popup";

import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { isObject } from "utils";

import {
  Select,
  CreatableSelect,
  Container,
  ClearButtonContainer,
  Wrapper,
  Label,
  MultiValuePlaceholder,
  MultiValueContainer,
  MultiValues,
  MultiValue,
  MultiValueClear,
} from "./styles";

type DictionaryItem = {
  value: any;
  label: string;
};

type DropdownDataProvider = {
  options: Array<DictionaryItem>;
  isLoading: boolean;
};

export type FilterOptionsType = { data: any; label: string; value: any };

export type DropdownExternalProps = {
  options?: Array<DictionaryItem>;
  disabled?: boolean;
  searchable?: boolean;
  multi?: boolean;
  multiTagMode?: boolean;
  loading?: boolean;
  placeholder?: string;
  provider?: DropdownDataProvider;
  value: DictionaryItem;
  label?: string;
  hideIndicator?: boolean;
  filterOption?: (options: FilterOptionsType, search: string) => boolean;
  hideSelectedOptions?: boolean;
  menuPosition?: string;
  isClearable?: boolean;
  onInputChange?: (value: string) => void;
  maxMenuHeight?: number;
  withClearButton?: boolean;
  shouldShowDeleteOptionIcon?: (option: DictionaryItem) => boolean;
  required?: boolean;
  isCreatable?: boolean;
};

type CreatableSelectProps = {
  onCreateOption: (
    option: string,
  ) => Promise<DictionaryItem | undefined> | void;
  onRemoveOption?: (id: string) => Promise<void> | void;
  deletePopupProps: {
    title: string;
    getText: (optionName: string) => string;
    acceptText: string;
    declineText: string;
  };
};

type Props = {
  onChange: (item: ValueType<DictionaryItem>) => void;
  error?: string | Object;
  input: FieldInputProps<string>;
} & DropdownExternalProps &
  CreatableSelectProps &
  SnackbarContextProps;

const MultiValueContainerComp = (multiTagMode?: boolean) => (
  props: MultiValueProps<DictionaryItem>,
) => {
  const { MultiValueContainer } = components;
  const {
    selectProps: { inputValue, value },
  } = props;

  return !inputValue && !value ? (
    <MultiValuePlaceholder>
      {props.selectProps.placeholder}
    </MultiValuePlaceholder>
  ) : !multiTagMode ? (
    <MultiValueContainer {...props} />
  ) : null;
};

const MultiValueComponent = (
  onRemoveClick: (item: ValueType<DictionaryItem>) => void,
) => (props: any) => {
  const { MultiValue } = components;
  const { data } = props;

  const onRemoveTest = onRemoveClick(data);

  return (
    <MultiValue
      {...props}
      removeProps={{ ...props.removeProps, onClick: onRemoveTest }}
    >
      <TrimmedText text={data.label} lines={1}>
        {data.label}
      </TrimmedText>
    </MultiValue>
  );
};

const OptionDefaultComp = (props: OptionProps<DictionaryItem>) => {
  const { Option } = components;
  const {
    data: { label },
  } = props;

  return (
    <Option {...props}>
      <TrimmedText text={label} lines={1}>
        {label}
      </TrimmedText>
    </Option>
  );
};

const Control = (hasError?: boolean, label?: string) => (props: any) => {
  const { Control } = components;

  return (
    <>
      <Label
        isFloating={props.isFocused || props.hasValue}
        hasValue={props.hasValue && !props.isFocused}
        hasError={hasError}
      >
        {label}
      </Label>
      <Control {...props} />
    </>
  );
};

const SingleValue = (props: any) => {
  const { SingleValue } = components;
  const {
    data: { label },
  } = props;

  return (
    <SingleValue {...props}>
      <TrimmedText text={label} lines={1}>
        {label}
      </TrimmedText>
    </SingleValue>
  );
};

@observer
class Dropdown extends React.Component<Props> {
  @observable optionToDelete: DictionaryItem | undefined = undefined;

  @computed
  get values() {
    const { provider, options, input } = this.props;
    const currentOptions = provider?.options || options;
    const value = input?.value || [];
    return value.map(item =>
      currentOptions?.find(findItem =>
        isObject(item)
          ? findItem.value.id === item?.id
          : findItem.value === item,
      ),
    );
  }

  @computed
  get value() {
    const { provider, options, input } = this.props;
    const currentOptions = provider?.options || options;
    return (
      currentOptions?.find(item =>
        isObject(item.value)
          ? item.value.id === input?.value?.id
          : item.value === input?.value,
      ) || input?.value
    );
  }

  get fixedOptions() {
    const { provider, options } = this.props;
    const currentOptions = provider?.options || options;

    return currentOptions?.filter(item => item?.isFixed);
  }

  get optionComponent() {
    return OptionDefaultComp;
  }

  get controlComponent() {
    const { label, required } = this.props;

    const labelText = required ? `${label} *` : label;

    return Control(!!this.error, labelText);
  }

  get multiValueContainerComponent() {
    const { multiTagMode } = this.props;

    return MultiValueContainerComp(multiTagMode);
  }

  get multiValueComponent() {
    return MultiValueComponent(this.onRemoveItem);
  }

  @computed
  get components() {
    const { hideIndicator } = this.props;

    const components = {
      MultiValueContainer: this.multiValueContainerComponent,
      MultiValue: this.multiValueComponent,
      Option: OptionDefaultComp,
      Control: this.controlComponent,
      SingleValue,
    };
    const withoutIndicator = {
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
    };
    return hideIndicator ? { ...components, ...withoutIndicator } : components;
  }

  get error() {
    const { error } = this.props;

    if (!error) {
      return undefined;
    }

    return isObject(error) ? Object.values(error).join("") : error;
  }

  onChange = (
    value: ValueType<DictionaryItem>,
    { action }: { action?: string },
  ) => {
    action !== "clear"
      ? this.props.onChange(value)
      : this.props.onChange(this.fixedOptions);
  };

  onRemoveItem = (item: ValueType<DictionaryItem>) => () => {
    const { isCreatable } = this.props;

    if (isCreatable && !item?.value?.isNewOption) {
      this.optionToDelete = item;
    } else {
      this.removeItem(item);
    }
  };

  removeItem = (value: ValueType<DictionaryItem>) => {
    const values = this.values.filter(item => item !== value);
    this.props.onChange(values);
  };

  onClearValues = () => {
    this.props.onChange([]);
  };

  getOptionValue = (option: DictionaryItem) => {
    return option?.value && isObject(option.value)
      ? option?.value?.id
      : option.value;
  };

  onCreateOption = async (option: string) => {
    const { onCreateOption, multi, setMessage } = this.props;

    try {
      const newOption = await onCreateOption(option);

      if (newOption) {
        this.onChange(multi ? [...this.values, newOption] : newOption, {});
      }
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onRemoveOption = async () => {
    const { onRemoveOption, multi, setMessage } = this.props;

    if (!this.optionToDelete) {
      return;
    }

    const optionToDelete = this.optionToDelete;

    try {
      await onRemoveOption?.(optionToDelete.value);
      this.closeRemoveOptionPopup();

      if (multi) {
        this.removeItem(optionToDelete);
      } else {
        this.onChange(undefined, {});
      }
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  openRemoveOptionPopup = (option: DictionaryItem) => {
    this.optionToDelete = option;
  };

  closeRemoveOptionPopup = () => {
    this.optionToDelete = undefined;
  };

  render() {
    const {
      disabled,
      searchable,
      multi,
      multiTagMode,
      loading,
      placeholder,
      input,
      provider,
      options,
      filterOption,
      hideSelectedOptions,
      onInputChange,
      maxMenuHeight,
      menuPosition,
      isClearable = false,
      withClearButton,
      shouldShowDeleteOptionIcon,
      isCreatable,
      deletePopupProps,
    } = this.props;

    const SelectComponent = isCreatable ? CreatableSelect : Select;

    return (
      <>
        {!!this.optionToDelete && (
          <Popup onClose={this.closeRemoveOptionPopup}>
            <ConfirmationDialog
              title={deletePopupProps.title}
              text={deletePopupProps.getText(this.optionToDelete.label)}
              acceptText={deletePopupProps.acceptText}
              declineText={deletePopupProps.declineText}
              onAccept={this.onRemoveOption}
              onDecline={this.closeRemoveOptionPopup}
              isLoading={provider?.isLoading || loading}
            />
          </Popup>
        )}
        <Container>
          <Wrapper>
            <SelectComponent
              {...input}
              key={multi ? this.values : this.value}
              value={multi ? this.values : this.value}
              options={provider?.options || options}
              onChange={this.onChange}
              isDisabled={provider?.isLoading || loading || disabled}
              isSearchable={searchable}
              isMulti={multi}
              isLoading={provider?.isLoading || loading}
              placeholder={placeholder}
              error={this.error}
              menuPosition={menuPosition || "fixed"}
              components={this.components}
              filterOption={filterOption}
              hideSelectedOptions={hideSelectedOptions}
              isClearable={isClearable}
              onInputChange={onInputChange}
              getOptionValue={this.getOptionValue}
              maxMenuHeight={maxMenuHeight}
              onCreateOption={isCreatable ? this.onCreateOption : undefined}
              noOptionsMessage={isCreatable ? () => null : undefined}
              blurInputOnSelect
            />
          </Wrapper>
          {multi && multiTagMode && this.values.length > 0 && (
            <MultiValueContainer>
              <MultiValues withClearButton={!!withClearButton}>
                {this.values.map(item => (
                  <MultiValue
                    key={isObject(item) ? item?.value?.id : item?.value}
                    hideClearIcon={
                      shouldShowDeleteOptionIcon &&
                      !shouldShowDeleteOptionIcon(item)
                    }
                  >
                    <Typography variant="subtitle1">{item?.label}</Typography>
                    {!disabled && (
                      <MultiValueClear onClick={this.onRemoveItem(item)}>
                        <DeleteOutlineIcon />
                      </MultiValueClear>
                    )}
                  </MultiValue>
                ))}
              </MultiValues>
              {withClearButton && (
                <ClearButtonContainer>
                  <Button
                    onClick={this.onClearValues}
                    startIcon={<DeleteOutlineIcon />}
                  >
                    Clear
                  </Button>
                </ClearButtonContainer>
              )}
            </MultiValueContainer>
          )}
          {this.error && <FormControlError error={this.error} />}
        </Container>
      </>
    );
  }
}

export default withGlobalSnackbar(Dropdown);
